/**
 * DatabaseView Component
 * 
 * Displays the Parse Server database browser with:
 * - High-performance data table using TanStack Table
 * - Type-aware cell rendering for all Parse types
 * - Column resizing
 * - Click-to-expand for complex values
 * - Glassmorphism styling
 * 
 * The class selector is in the header (AppPanelHeader).
 */

import { useMemo } from 'react';
import { ParseClass, ParseRow, ParseSchema } from '../../../../types/AppPanel';
import DataTable from './DataTable';

interface DatabaseViewProps {
  /** List of available classes in the database */
  classes?: ParseClass[];
  /** Currently selected class name (controlled from header) */
  selectedClass?: string;
  /** Rows from the selected class */
  rows?: ParseRow[];
  /** Total count of rows in the class (for pagination info) */
  totalCount?: number;
  /** Whether data is loading */
  isLoading?: boolean;
  /** URL to open full dashboard */
  dashboardUrl?: string;
  /** Full schemas from Parse Server */
  schemas?: ParseSchema[];
  /** Callback when a pointer is clicked */
  onPointerClick?: (pointer: { className: string; objectId: string }) => void;
  /** Callback when a relation is clicked */
  onRelationClick?: (targetClass: string, sourceObjectId: string, field: string) => void;
  /** Current page (0-indexed) */
  currentPage?: number;
  /** Number of rows per page */
  pageSize?: number;
  /** Callback when page changes */
  onPageChange?: (page: number) => void;
  /** Current sort field (server-side) */
  sortField?: string;
  /** Current sort direction (server-side) */
  sortDirection?: 'asc' | 'desc';
  /** Callback when sort changes (server-side) */
  onSortChange?: (field: string, direction: 'asc' | 'desc') => void;
}

export default function DatabaseView({
  classes,
  selectedClass,
  rows,
  totalCount,
  isLoading,
  dashboardUrl,
  schemas,
  onPointerClick,
  onRelationClick,
  currentPage = 0,
  pageSize = 100,
  onPageChange,
  sortField,
  sortDirection,
  onSortChange,
}: DatabaseViewProps) {
  // Get schema for selected class
  const currentSchema = useMemo(() => {
    if (!schemas || !selectedClass) return null;
    return schemas.find(s => s.className === selectedClass) || null;
  }, [schemas, selectedClass]);

  // No classes available - empty state
  if (!classes || classes.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 gap-4">
        <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
          <DatabaseIcon />
        </div>
        <p className="text-white/40 text-sm text-center">
          No database classes yet.<br />
          Classes will appear here once created.
        </p>
      </div>
    );
  }

  // No class selected
  if (!selectedClass) {
    return (
      <div className="flex-1 flex items-center justify-center text-white/40 text-sm">
        Select a class from the dropdown above
      </div>
    );
  }

  // No schema found for selected class (shouldn't happen normally)
  if (!currentSchema) {
    return (
      <div className="flex-1 flex items-center justify-center text-white/40 text-sm">
        Schema not found for {selectedClass}
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Data Table */}
      <DataTable
        data={rows || []}
        schema={currentSchema}
        dashboardUrl={dashboardUrl}
        onPointerClick={onPointerClick}
        onRelationClick={onRelationClick}
        isLoading={isLoading}
        sortField={sortField}
        sortDirection={sortDirection}
        onSortChange={onSortChange}
      />

      {/* Footer with row count and pagination */}
      {rows && rows.length > 0 && (
        <div className="px-4 py-2 bg-black/20 border-t border-white/5 text-sm text-white/50 flex items-center gap-4">
          {/* Row count info */}
          <span>
            {totalCount && totalCount > pageSize ? (
              <>
                Showing {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalCount)} of {totalCount} rows
              </>
            ) : (
              <>Showing {rows.length} rows</>
            )}
          </span>

          {/* Pagination controls - inline with row count */}
          {totalCount && totalCount > pageSize && onPageChange && (
            <div className="flex items-center gap-2">
              {/* Previous button */}
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 0}
                className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30 
                           disabled:cursor-not-allowed transition-colors text-white/70"
                title="Previous page"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>

              {/* Page indicator */}
              <span className="text-white/60">
                Page {currentPage + 1} of {Math.ceil(totalCount / pageSize)}
              </span>

              {/* Next button */}
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={(currentPage + 1) * pageSize >= totalCount}
                className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30 
                           disabled:cursor-not-allowed transition-colors text-white/70"
                title="Next page"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DatabaseIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white/30">
      <ellipse cx="13" cy="6" rx="9" ry="3" stroke="currentColor" strokeWidth="2"/>
      <path d="M4 6V13C4 14.6569 8.02944 16 13 16C17.9706 16 22 14.6569 22 13V6" stroke="currentColor" strokeWidth="2"/>
      <path d="M4 13V20C4 21.6569 8.02944 23 13 23C17.9706 23 22 21.6569 22 20V13" stroke="currentColor" strokeWidth="2"/>
    </svg>
  );
}

